/**
 * Step 5: Imitation Learning Configuration
 * Observation space, action space, and reward settings
 */

import {
  Box,
  Title,
  Text,
  Stack,
  Paper,
  Switch,
  MultiSelect,
  Select,
  NumberInput,
  Textarea,
  Group,
  Code,
  Badge,
} from "@mantine/core";
import { useConfigureStore } from "../ConfigureStore";

export function ImitationStep() {
  const {
    joints,
    ilObservation,
    ilAction,
    updateILObservation,
    updateILAction,
  } = useConfigureStore();

  const actuatedJoints = joints.filter((j) => j.type !== "fixed");
  const jointOptions = actuatedJoints.map((j) => ({ value: j.name, label: j.name }));

  const actionTypeOptions = [
    { value: "joint_positions", label: "Joint Positions" },
    { value: "joint_velocities", label: "Joint Velocities" },
    { value: "end_effector_delta", label: "End Effector Delta" },
  ];

  // Calculate observation dimensions
  const calcObsDim = () => {
    let dim = 0;
    if (ilObservation.includeJointPositions) dim += actuatedJoints.length;
    if (ilObservation.includeJointVelocities) dim += actuatedJoints.length;
    if (ilObservation.includeEndEffectorPose) dim += 7; // pos(3) + quat(4)
    dim += ilObservation.customObservations.length;
    return dim;
  };

  // Calculate action dimensions
  const calcActionDim = () => {
    return ilAction.selectedJoints.length;
  };

  return (
    <Stack gap="lg">
      <Box>
        <Title order={3} mb="xs">
          Imitation Learning Configuration
        </Title>
        <Text c="dimmed" size="sm">
          Configure observation and action spaces for reinforcement learning and imitation learning.
        </Text>
      </Box>

      {/* Observation Space */}
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Observation Space
        </Title>
        <Stack gap="md">
          <Switch
            label="Include Joint Positions"
            description={`Adds ${actuatedJoints.length} dimensions`}
            checked={ilObservation.includeJointPositions}
            onChange={(e) =>
              updateILObservation({
                includeJointPositions: e.currentTarget.checked,
              })
            }
          />

          <Switch
            label="Include Joint Velocities"
            description={`Adds ${actuatedJoints.length} dimensions`}
            checked={ilObservation.includeJointVelocities}
            onChange={(e) =>
              updateILObservation({
                includeJointVelocities: e.currentTarget.checked,
              })
            }
          />

          <Switch
            label="Include End Effector Pose"
            description="Adds 7 dimensions (position + quaternion)"
            checked={ilObservation.includeEndEffectorPose}
            onChange={(e) =>
              updateILObservation({
                includeEndEffectorPose: e.currentTarget.checked,
              })
            }
          />

          <Textarea
            label="Custom Observations (one per line)"
            description="Additional observation variables"
            placeholder="gripper_force&#10;contact_sensor"
            value={ilObservation.customObservations.join("\n")}
            onChange={(e) =>
              updateILObservation({
                customObservations: e.currentTarget.value
                  .split("\n")
                  .filter((s) => s.trim()),
              })
            }
            minRows={3}
          />

          <Group>
            <Badge size="lg" variant="light">
              Total Observation Dim: {calcObsDim()}
            </Badge>
          </Group>
        </Stack>
      </Paper>

      {/* Action Space */}
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Action Space
        </Title>
        <Stack gap="md">
          <Select
            label="Action Type"
            description="How actions are interpreted"
            data={actionTypeOptions}
            value={ilAction.actionType}
            onChange={(value) =>
              updateILAction({
                actionType: (value as typeof ilAction.actionType) || "joint_positions",
              })
            }
          />

          <MultiSelect
            label="Controlled Joints"
            description="Joints that can be controlled by the policy"
            data={jointOptions}
            value={ilAction.selectedJoints}
            onChange={(value) => updateILAction({ selectedJoints: value })}
            searchable
          />

          <NumberInput
            label="Action Scale"
            description="Multiplier for action values"
            value={ilAction.actionScale}
            onChange={(value) =>
              updateILAction({
                actionScale: typeof value === "number" ? value : 1.0,
              })
            }
            min={0.01}
            max={10}
            step={0.1}
            decimalScale={2}
          />

          <Group>
            <Badge size="lg" variant="light">
              Total Action Dim: {calcActionDim()}
            </Badge>
          </Group>
        </Stack>
      </Paper>

      {/* Configuration Preview */}
      <Paper withBorder p="md" bg="gray.0">
        <Title order={5} mb="sm">
          Configuration Preview (YAML)
        </Title>
        <Code block style={{ whiteSpace: "pre-wrap" }}>
          {`# Imitation Learning Configuration
robot:
  observation_space:
    dim: ${calcObsDim()}
    include_joint_positions: ${ilObservation.includeJointPositions}
    include_joint_velocities: ${ilObservation.includeJointVelocities}
    include_end_effector_pose: ${ilObservation.includeEndEffectorPose}
${
  ilObservation.customObservations.length > 0
    ? `    custom_observations:\n${ilObservation.customObservations.map((o) => `      - ${o}`).join("\n")}`
    : ""
}

  action_space:
    dim: ${calcActionDim()}
    type: ${ilAction.actionType}
    scale: ${ilAction.actionScale}
    joints:
${ilAction.selectedJoints.map((j) => `      - ${j}`).join("\n")}

# Training hints
training:
  horizon: 1000
  batch_size: 256
  learning_rate: 3e-4`}
        </Code>
      </Paper>
    </Stack>
  );
}
